import { IOpportunityChange } from '../../../shared/model/opportunity-change.model';
import { IOpportunity } from '../../../shared/model/opportunity.model';
import { Action, createReducer, on } from '@ngrx/store';
import { IOrganizationChange } from '../../../shared/model/organization-change.model';
import { IOrganization } from '../../../shared/model/organization.model';
import { IServiceRegistration } from '../../../shared/model/service-registration.model';
import { ISupervisor } from '../../../shared/model/supervisor.model';
import { ITableOptions } from '../../../shared/model/table-options.model';
import { TableData } from '../../../shared/model/tableData.model';
import { ITermEvent } from '../../../shared/model/term-event.model';
import { ITerm } from '../../../shared/model/term.model';
import {
  forceOpportunityApprovalTableRefreshAction,
  setAdminOpportunityApprovalsStateAction,
  setAdminOpportunityChangeApprovalsStateAction,
  setAdminOrganizationApprovalsStateAction,
  setAdminOrganizationChangeApprovalsStateAction,
  setAdminOrganizationDashboardStateAction,
  setAdminOrganizationEventsStateAction,
  setAdminPlacementDashboardStateAction,
  setAdminPlacementEventsStateAction,
  setAdminSupervisorDashboardStateAction,
  setAdminSupervisorEventsStateAction,
  setAdminUserDashboardStateAction,
  setAdminUserVolunteerDashboardStateAction,
  setTermStateAction,
  setTermEventStateAction,
  setSubmittedEvalsStateAction,
  setOpportunityIdAndIsLeadForTableStateTablesAction,
  refreshSupervisorTableStateTablesForNewOpportunityAction,
  setActiveVolunteersStateAction,
  refreshSupervisorTableStateTablesForSameOpportunityAction,
  forceOrganizationApprovalTableRefreshAction,
  updateOpportunityOnApprovalTableAction,
  forcePlacementsTableRefreshAction,
  forceUserTableRefreshAction,
  setAdminOpportunityDashboardStateAction,
  setAdminOpportunityEventsStateAction,
} from './table.actions';
import { ITableState, TableSearchState } from './table.state';
import { stateHelperFunctions } from "../stateHelperFunctions";

const initialTableOptions = <ITableOptions>{
  currentPage: 1,
  pageSize: 25,
  sortColumn: undefined,
  totalRecords: 0,
};

export const initialTableState: ITableState = {
  organizationTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions,
    organizationList: <IOrganization[]>[]
  },

  organizationEventsTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions
  },

  supervisorTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions,
    supervisorList: <ISupervisor[]>[],
    organizationList: <IOrganization[]>[]
  },

  supervisorEventsTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions
  },

  placementTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions,
    shouldRefreshTableData: false
  },

  placementEventsTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions,
    serviceList: <IServiceRegistration[]>[]
  },

  userTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions,
    shouldRefreshTableData: false
  },

  userVolunteerTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions,
    shouldRefreshTableData: false
  },

  opportunityApprovalsTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions,
    organizationList: <IOrganization[]>[],
    opportunityList: <IOpportunity[]>[]
  },

  opportunityChangeApprovalsTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions,
    oppChangeList: <IOpportunityChange[]>[],
    opportunityList: <IOpportunity[]>[],
    supervisorList: <ISupervisor[]>[]
  },

  organizationApprovalsTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions,
    supervisorList: <ISupervisor[]>[],
    organizationList: <IOrganization[]>[]
  },

  organizationChangeApprovalsTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions,
    orgChangeList: <IOrganizationChange[]>[],
    supervisorList: <ISupervisor[]>[],
    organizationList: <IOrganization[]>[]
  },

  termTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions,
    termList: <ITerm[]>[],
    activeTermList: <ITerm[]>[]
  },

  termEventsTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions,
    termList: <ITerm[]>[],
    termEventList: <ITermEvent[]>[]
  },

  submittedEvalsTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions,
    isLead: false,
    opportunityId: "",
    shouldRefreshTableData: false
  },

  activeVolunteersTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions,
    isLead: false,
    opportunityId: "",
    shouldRefreshTableData: true,
    serviceList: <IServiceRegistration[]>[]
  },

  opportunityTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions,
  },

  opportunityEventsTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions,
  },

  registrationRequestsTab: <TableSearchState>{
    textFilter: "",
    showInactive: false,
    results: <TableData[]>[],
    tableOptions: initialTableOptions,
    isLead: false,
    opportunityId: "",
    shouldRefreshTableData: false,
    serviceList: <IServiceRegistration[]>[]
  }
};

const tableReducer = createReducer(
  initialTableState,

  on(setAdminOrganizationDashboardStateAction,
    (state, action): ITableState => ({
      ...state,
      organizationTab: action.newState
    })
  ),

  on(setAdminSupervisorDashboardStateAction,
    (state, action): ITableState => ({
      ...state,
      supervisorTab: action.newState
    })
  ),

  on(setAdminPlacementDashboardStateAction,
    (state, action): ITableState => ({
      ...state,
      placementTab: action.newState
    })
  ),

  on(setAdminUserDashboardStateAction,
    (state, action): ITableState => ({
      ...state,
      userTab: action.newState
    })
  ),

  on(setAdminUserVolunteerDashboardStateAction,
    (state, action): ITableState => ({
      ...state,
      userVolunteerTab: action.newState
    })
  ),

  on(setAdminOrganizationEventsStateAction,
    (state, action): ITableState => ({
      ...state,
      organizationEventsTab: action.newState
    })
  ),

  on(setAdminSupervisorEventsStateAction,
    (state, action): ITableState => ({
      ...state,
      supervisorEventsTab: action.newState
    })
  ),

  on(setAdminPlacementEventsStateAction,
    (state, action): ITableState => ({
      ...state,
      placementEventsTab: action.newState
    })
  ),

  on(setAdminOpportunityApprovalsStateAction,
    (state, action): ITableState => ({
      ...state,
      opportunityApprovalsTab: action.newState
    })
  ),

  on(setAdminOpportunityChangeApprovalsStateAction,
    (state, action): ITableState => ({
      ...state,
      opportunityChangeApprovalsTab: action.newState
    })
  ),

  on(setAdminOrganizationApprovalsStateAction,
    (state, action): ITableState => ({
      ...state,
      organizationApprovalsTab: action.newState
    })
  ),

  on(setAdminOrganizationChangeApprovalsStateAction,
    (state, action): ITableState => ({
      ...state,
      organizationChangeApprovalsTab: action.newState
    })
  ),

  on(setTermStateAction,
    (state, action): ITableState => ({
      ...state,
      termTab: action.newState
    })
  ),

  on(setTermEventStateAction,
    (state, action): ITableState => ({
      ...state,
      termEventsTab: action.newState
    })
  ),

  on(setSubmittedEvalsStateAction,
    (state, action): ITableState => ({
      ...state,
      submittedEvalsTab: action.newState
    })
  ),

  on(setActiveVolunteersStateAction,
    (state, action): ITableState => ({
      ...state,
      activeVolunteersTab: action.newState
    })
  ),

  on(forceOpportunityApprovalTableRefreshAction,
    (state, action): ITableState => ({
      ...state,
      opportunityApprovalsTab: {
        ...state.opportunityApprovalsTab,
        shouldRefreshTableData: true
      }
    })
  ),

  on(updateOpportunityOnApprovalTableAction,
    (state, action): ITableState => ({
      ...state,
      opportunityApprovalsTab: {
        ...state.opportunityApprovalsTab,
        results: stateHelperFunctions.replaceItemInArray(state.opportunityApprovalsTab.results, action.data),
      }
    })
  ),

  on(forceOrganizationApprovalTableRefreshAction,
    (state, action): ITableState => ({
      ...state,
      organizationApprovalsTab: {
        ...state.organizationApprovalsTab,
        shouldRefreshTableData: true
      }
    })
  ),

  on(forcePlacementsTableRefreshAction,
    (state, action): ITableState => ({
      ...state,
      placementTab: {
        ...state.placementTab,
        shouldRefreshTableData: true
      }
    })
  ),

  on(forceUserTableRefreshAction,
    (state, action): ITableState => ({
      ...state,
      userTab: {
        ...state.userTab,
        shouldRefreshTableData: true
      }
    })
  ),

  on(refreshSupervisorTableStateTablesForNewOpportunityAction,
    (state, action): ITableState => ({
      ...state,
      submittedEvalsTab: {
        ...initialTableState.submittedEvalsTab,
        shouldRefreshTableData: true
      },
      activeVolunteersTab: {
        ...initialTableState.activeVolunteersTab,
        shouldRefreshTableData: true
      }
    })
  ),

  on(refreshSupervisorTableStateTablesForSameOpportunityAction,
    (state, action): ITableState => ({
      ...state,
      submittedEvalsTab: {
        ...state.submittedEvalsTab,
        shouldRefreshTableData: true
      },
      activeVolunteersTab: {
        ...state.activeVolunteersTab,
        shouldRefreshTableData: true
      },
      registrationRequestsTab: {
        ...state.registrationRequestsTab,
        shouldRefreshTableData: true
      }
    })
  ),

  on(setOpportunityIdAndIsLeadForTableStateTablesAction,
    (state, action): ITableState => ({
      ...state,
      submittedEvalsTab: {
        ...state.submittedEvalsTab,
        isLead: action.isLead,
        opportunityId: action.id
      },
      activeVolunteersTab: {
        ...state.activeVolunteersTab,
        isLead: action.isLead,
        opportunityId: action.id
      },
      registrationRequestsTab: {
        ...state.registrationRequestsTab,
        isLead: action.isLead,
        opportunityId: action.id
      }
    })
  ),

  on(setAdminOpportunityDashboardStateAction,
    (state, action): ITableState => ({
      ...state,
      opportunityTab: action.newState
    })
  ),

  on(setAdminOpportunityEventsStateAction,
    (state, action): ITableState => ({
      ...state,
      opportunityEventsTab: action.newState
    })
  ),

);

export function tableReducers(state: ITableState, action: Action) {
  return tableReducer(state, action);
}
